import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Skeleton, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MapIcon from "@mui/icons-material/Map";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { get_top_banner } from "../../api/ControllApi";
import { useCachedApi } from "../../hooks/useCachedApi";
import GeneralEnquiryModal from "../../components/common/GeneralEnquiryModal";

function NextArrow(props) {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        right: { xs: 10, md: 30 },
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
        bgcolor: "#FFFFFF",
        color: "#163F1D",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        width: 44,
        height: 44,
        "&:hover": { bgcolor: "#FFFFFF", transform: "translateY(-50%) scale(1.05)" },
      }}
    >
      <ArrowForwardIosIcon sx={{ fontSize: "1.1rem" }} />
    </IconButton>
  );
}

function PrevArrow(props) {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        left: { xs: 10, md: 30 },
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
        bgcolor: "#FFFFFF",
        color: "#163F1D",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        width: 44,
        height: 44,
        "&:hover": { bgcolor: "#FFFFFF", transform: "translateY(-50%) scale(1.05)" },
      }}
    >
      <ArrowBackIosNewIcon sx={{ fontSize: "1.1rem" }} />
    </IconButton>
  );
}

const sliderSettings = {
  dots: false,
  infinite: true,
  autoplay: true,
  speed: 600,
  autoplaySpeed: 4000,
  slidesToShow: 1,
  slidesToScroll: 1,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
};

export default function Herobanner() {
  const navigate = useNavigate();
  const { requestApi } = useCachedApi();
  const [topBanner, setTopBanner] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    requestApi("top_banner", get_top_banner)
      .then((res) => {
        const apiBanners = res?.data ?? [];
        if (apiBanners.length >= 1) {
          setTopBanner(apiBanners);
        } else {
          setTopBanner([
            { image: "/assets/images/landing_bg.png" },
            { image: "/assets/images/hero_premium_bg.jpg" },
            { image: "/assets/images/residential_bg.png" },
            { image: "/assets/images/main-banner.png" },
          ]);
        }
      })
      .catch(() => {
        setTopBanner([
          { image: "/assets/images/landing_bg.png" },
          { image: "/assets/images/hero_premium_bg.jpg" },
          { image: "/assets/images/residential_bg.png" },
          { image: "/assets/images/main-banner.png" },
        ]);
      })
      .finally(() => setLoading(false));
  }, [requestApi]);

  const settings = {
    ...sliderSettings,
    beforeChange: (_, next) => setCurrentSlide(next),
  };

  return (
    <Box sx={{ position: "relative", width: "100%", overflow: "hidden", bgcolor: "#FFFFFF" }}>
      {loading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          sx={{
            aspectRatio: { xs: "16 / 9", sm: "2.4 / 1", md: "1920 / 588" },
            height: "auto",
          }}
          animation="wave"
        />
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: { xs: "16 / 9", sm: "2.4 / 1", md: "1920 / 588" },
            overflow: "hidden",
            bgcolor: "#FFFFFF",
            "& .slick-slider, & .slick-list, & .slick-track": {
              height: "100%",
            },
            "& .slick-slide > div": {
              height: "100%",
            },
          }}
        >
          {/* Main Slider */}
          <Slider {...settings}>
            {topBanner.map((slide, idx) => (
              <Box
                key={idx}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  outline: "none",
                  display: "flex !important",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={slide?.image || "/assets/images/landing_bg.png"}
                  alt="Greenheap Banner"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/assets/images/landing_bg.png";
                  }}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                    display: "block",
                  }}
                />
              </Box>
            ))}
          </Slider>

          {/* Right Side Action Buttons Container (Without Dark Green Overlay) */}
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: 8, sm: 14, md: 20 },
              right: { xs: 8, sm: 18, md: 28 },
              zIndex: 4,
              display: "flex",
              pointerEvents: "none",
            }}
          >
            {/* Buttons Row */}
            <Box
              sx={{
                pointerEvents: "auto",
                display: "flex",
                flexWrap: "wrap",
                gap: { xs: 1, sm: 1.5, md: 2 },
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              {/* Coral Red Enquiry Button */}
              <Button
                variant="contained"
                onClick={() => setEnquiryModalOpen(true)}
                startIcon={<AutoAwesomeIcon sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }} />}
                sx={{
                  bgcolor: "#FF5757",
                  color: "#FFFFFF",
                  borderRadius: "50px",
                  px: { xs: 1.8, sm: 2.6, md: 3.2 },
                  py: { xs: 0.6, sm: 0.9, md: 1.1 },
                  fontWeight: 800,
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                  textTransform: "none",
                  boxShadow: "0 6px 20px rgba(255, 87, 87, 0.4)",
                  fontFamily: "'Outfit', sans-serif",
                  "&:hover": { bgcolor: "#E04848" },
                }}
              >
                Enquiry
              </Button>

              {/* Bright Lime Green Add Your Property Button */}
              <Button
                variant="contained"
                onClick={() => navigate("/dashboard/add-property")}
                startIcon={<MapIcon sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }} />}
                sx={{
                  bgcolor: "#59B400",
                  color: "#FFFFFF",
                  borderRadius: "50px",
                  px: { xs: 1.8, sm: 2.6, md: 3.2 },
                  py: { xs: 0.6, sm: 0.9, md: 1.1 },
                  fontWeight: 800,
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                  textTransform: "uppercase",
                  boxShadow: "0 6px 20px rgba(89, 180, 0, 0.4)",
                  fontFamily: "'Outfit', sans-serif",
                  "&:hover": { bgcolor: "#4B9800" },
                }}
              >
                ADD YOUR PROPERTY
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* General Enquiry Dialog Form */}
      <GeneralEnquiryModal
        open={enquiryModalOpen}
        onClose={() => setEnquiryModalOpen(false)}
      />
    </Box>
  );
}
